<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { loginUser, registerUser } from '@/lib/apiClient';
import { useAuth } from '@/composables/useAuth';

const router = useRouter();
const toast = useToast();
const { setSession } = useAuth();

const registerVisible = ref(false);
const loginVisible = ref(false);
const submitting = ref(false);
const errorMessage = ref<string | null>(null);

const registerForm = ref({ nickname: '', password: '' });
const loginForm = ref({ nickname: '', password: '' });

function openRegister() {
  errorMessage.value = null;
  registerForm.value = { nickname: '', password: '' };
  registerVisible.value = true;
}

function openLogin() {
  errorMessage.value = null;
  loginForm.value = { nickname: '', password: '' };
  loginVisible.value = true;
}

async function handleRegister() {
  submitting.value = true;
  errorMessage.value = null;

  try {
    const result = await registerUser(
      registerForm.value.nickname,
      registerForm.value.password,
    );
    setSession(result.token, result.user);
    registerVisible.value = false;
    toast.add({
      severity: 'success',
      summary: 'Готово',
      detail: `${result.user.nickname}, записали. Не теряй пароль.`,
      life: 3000,
    });
    await router.push({ name: 'home' });
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Ошибка регистрации';
  } finally {
    submitting.value = false;
  }
}

async function handleLogin() {
  submitting.value = true;
  errorMessage.value = null;

  try {
    const result = await loginUser(loginForm.value.nickname, loginForm.value.password);
    setSession(result.token, result.user);
    loginVisible.value = false;
    toast.add({
      severity: 'success',
      summary: 'С возвращением',
      detail: result.user.nickname,
      life: 3000,
    });
    await router.push({ name: 'home' });
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Ошибка входа';
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <main class="landing">
    <section class="landing__hero">
      <p class="landing__eyebrow">Убежище</p>
      <h1 class="landing__title">Причина сходки - День Рожденье Аллы</h1>
      <p class="landing__subtitle">Выбери, как ты здесь оказался — и продолжим.</p>
    </section>

    <section class="landing__tiles">
      <button type="button" class="tile tile--new" @click="openRegister">
        <span class="tile__icon pi pi-sparkles" />
        <span class="tile__label">Я здесь первый раз</span>
        <span class="tile__hint">Быстрая регистрация за пару секунд</span>
      </button>

      <button type="button" class="tile tile--return" @click="openLogin">
        <span class="tile__icon pi pi-sign-in" />
        <span class="tile__label">Я здесь уже был</span>
        <span class="tile__hint">Войти по никнейму и паролю</span>
      </button>
    </section>

    <Dialog
      v-model:visible="registerVisible"
      modal
      header="Быстрая регистрация"
      :style="{ width: 'min(420px, 92vw)' }"
      :draggable="false"
    >
      <div class="form">
        <Message v-if="errorMessage" severity="error" :closable="false">
          {{ errorMessage }}
        </Message>

        <label class="form__field">
          <span>Никнейм</span>
          <InputText
            v-model="registerForm.nickname"
            placeholder="Придумай уникальный ник"
            autocomplete="username"
            fluid
          />
        </label>

        <label class="form__field">
          <span>Пароль</span>
          <Password
            v-model="registerForm.password"
            placeholder="Любой пароль"
            :feedback="false"
            toggle-mask
            fluid
          />
        </label>
      </div>

      <template #footer>
        <Button
          label="Зарегистрироваться"
          icon="pi pi-check"
          :loading="submitting"
          @click="handleRegister"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="loginVisible"
      modal
      header="Вход"
      :style="{ width: 'min(420px, 92vw)' }"
      :draggable="false"
    >
      <div class="form">
        <Message v-if="errorMessage" severity="error" :closable="false">
          {{ errorMessage }}
        </Message>

        <label class="form__field">
          <span>Никнейм</span>
          <InputText
            v-model="loginForm.nickname"
            placeholder="Твой никнейм"
            autocomplete="username"
            fluid
          />
        </label>

        <label class="form__field">
          <span>Пароль</span>
          <Password
            v-model="loginForm.password"
            placeholder="Твой пароль"
            :feedback="false"
            toggle-mask
            fluid
          />
        </label>
      </div>

      <template #footer>
        <Button label="Войти" icon="pi pi-sign-in" :loading="submitting" @click="handleLogin" />
      </template>
    </Dialog>
  </main>
</template>
